import React, { useState, useEffect, useCallback, useRef } from 'react';
import { z } from 'zod';
import { post } from './AuthToken';
import setUiMessage from '../../Utilities/Tostify';
import Button from '../Button';

const ChangePasswordPopup = ({
  firstName = 'password',
  lastName = 'password',
  onClose,
}) => {
  const [oldPassword, setOldPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const inptRef = useRef();

  const passwordSchema = z
    .object({
      password: z
        .string()
        .min(8, 'Password must be at least 8 characters long.')
        .regex(
          /[!@#$%^&*(),.?":{}|<>]/,
          'Password must contain at least one special character.'
        )
        .regex(/\d/, 'Password must contain at least one numeric character.')
        .regex(/[A-Z]/, 'Password must contain at least one uppercase letter.')
        .refine(
          (value) =>
            !value.toLowerCase().includes(firstName.toLowerCase()) &&
            !value.toLowerCase().includes(lastName.toLowerCase()),
          'Password should not contain your first or last name.'
        ),
      oldPassword: z.string().min(1, 'Old Password is required.'),
      confirmPassword: z.string().min(1, 'Confirm Password is required.'),
    })
    .refine(
      (data) => data.password === data.confirmPassword,
      "Passwords don't match"
    );

  const validatePassword = useCallback(() => {
    const validation = passwordSchema.safeParse({
      oldPassword,
      password,
      confirmPassword,
    });
    if (!validation.success) {
      return validation.error.issues[0].message;
    }
    return '';
  }, [password, confirmPassword, oldPassword, passwordSchema]);
  useEffect(() => {
    inptRef.current.focus();
  }, []);

  useEffect(() => {
    const validationError = validatePassword();
    setError(validationError);
  }, [password, confirmPassword, oldPassword, validatePassword]);

  const handleSubmit = async () => {
    const validationError = validatePassword();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      const response = await post('api/patient/Patient/changePassword', {
        oldPwd: oldPassword,
        currentPwd: password,
      });
      if (response.status === 200) {
        if (response.data.status === '0') {
          setUiMessage('err', response.data.msg);
        } else {
          setUiMessage('suc', response.data.msg);
          onClose();
        }
      }
    } catch (error) {
      setUiMessage('err', error.response.data.message);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.popup}>
        <h2>Change Password</h2>
        <div style={styles.inputContainer}>
          <input
            type={showOldPassword ? 'text' : 'password'}
            placeholder="Enter old password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            style={styles.input}
            ref={inptRef}
          />
          <button
            type="button"
            onClick={() => setShowOldPassword(!showOldPassword)}
            style={styles.eyeButton}
          >
            {showOldPassword ? (
              <img
                src="./images/Access.png"
                alt="show password"
                width={'22px'}
                style={{
                  cursor: 'pointer',
                  position: 'absolute',
                  top: '-10px',
                  right: '0px',
                }}
              />
            ) : (
              '👁️'
            )}
          </button>
        </div>
        <div style={styles.inputContainer}>
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter new password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={styles.input}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            style={styles.eyeButton}
          >
            {showPassword ? (
              <img
                src="./images/Access.png"
                alt="show password"
                width={'22px'}
                style={{
                  cursor: 'pointer',
                  position: 'absolute',
                  top: '-10px',
                  right: '0px',
                }}
              />
            ) : (
              '👁️'
            )}
          </button>
        </div>
        <div style={styles.inputContainer}>
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            style={styles.input}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            style={styles.eyeButton}
          >
            {showConfirmPassword ? (
              <img
                src="./images/Access.png"
                alt="show password"
                width={'22px'}
                style={{
                  cursor: 'pointer',
                  position: 'absolute',
                  top: '-10px',
                  right: '0px',
                }}
              />
            ) : (
              '👁️'
            )}
          </button>
        </div>
        {error && <p style={styles.error}>{error}</p>}
        <div style={styles.actions}>
          <Button onClick={onClose} style={{ padding: '7px 15px' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant={'primary'}
            style={{ marginLeft: '10px', padding: '7px 15px' }}
          >
            Submit
          </Button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  popup: {
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '10px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    textAlign: 'center',
    width: '400px',
  },
  input: {
    width: '100%',
    padding: '10px',
    margin: '10px 0',
    fontSize: '16px',
    border: '1px solid #ccc',
    borderRadius: '5px',
    height: 'unset',
  },
  inputContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  eyeButton: {
    position: 'absolute',
    right: '10px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
  },
  error: {
    color: 'red',
    fontSize: '14px',
    margin: '10px 0',
  },
  actions: {
    display: 'flex',
    justifyContent: ' flex-end',
    marginTop: '20px',
    alignItems: 'center',
  },
};

export default ChangePasswordPopup;
